// Image renders that survive a lost connection (backend myapp/image_jobs.py; gpus report 55, 6 Oct 2026).
//
// The Create page names each render with a job id and sends it as `job_id`. When the backend's switch covers the
// user, the render no longer depends on the request: if the request is lost (Cloudflare's 100-s cut, a network
// change, a reload), the render carries on, and its answer waits on the server for at most an hour at
// GET /api/image_job/<id>. While the switch is off the server ignores the id and answers 404 for it, and the page
// behaves exactly as before.
//
// The pending ids live in localStorage (through safeStorage) so a reload can collect them too. A job the new backend
// took is noted with its order's id (`noteOrder`), and a reload follows that order on v2 instead.

export const PENDING_KEY = 'pc_image_jobs'
export const KEEP_MS = 60 * 60 * 1000 // the server keeps an answer no longer
export const POLL_DEADLINE_MS = 10 * 60 * 1000 // a render takes ~6 min at the very worst
const MAX_PENDING = 10

/** A new job id: 32 lowercase hex characters. */
export function newJobId(cryptoImpl = globalThis.crypto) {
  return cryptoImpl.randomUUID().replace(/-/g, '')
}

/** The remembered jobs still worth collecting, oldest first. */
export function readPending(getItem, now = Date.now()) {
  let list
  try {
    list = JSON.parse(getItem(PENDING_KEY) || '[]')
  } catch {
    return []
  }
  if (!Array.isArray(list)) return []
  return list.filter(
    (job) =>
      job &&
      typeof job.jobId === 'string' &&
      typeof job.apiBase === 'string' &&
      Number.isFinite(job.startedAt) &&
      now - job.startedAt < KEEP_MS,
  )
}

/** Remember a job before its request is sent. */
export function rememberJob(getItem, setItem, job, now = Date.now()) {
  const list = readPending(getItem, now).filter((j) => j.jobId !== job.jobId)
  list.push(job)
  setItem(PENDING_KEY, JSON.stringify(list.slice(-MAX_PENDING)))
}

/**
 * Note the order on the new backend (ai_app_backend) that a remembered job became, once it is placed: a reload then
 * follows that order on v2 (`collectPending` in Create.jsx), since the old backend never knew the job and answers 404.
 */
export function noteOrder(getItem, setItem, jobId, orderId, now = Date.now()) {
  const list = readPending(getItem, now)
  const job = list.find((j) => j.jobId === jobId)
  if (!job || typeof orderId !== 'string' || !orderId) return
  job.orderId = orderId
  setItem(PENDING_KEY, JSON.stringify(list))
}

/** Forget a job once its answer has been handled. */
export function forgetJob(getItem, setItem, jobId, now = Date.now()) {
  const list = readPending(getItem, now).filter((j) => j.jobId !== jobId)
  setItem(PENDING_KEY, JSON.stringify(list))
}

/** Whether a failed fetch means the connection went, not that the server answered. */
export function isConnectionLoss(error) {
  const msg = error?.message || ''
  return (
    error?.name === 'AbortError' ||
    error?.name === 'TypeError' ||
    msg.includes('ERR_CONNECTION_ABORTED') ||
    msg.includes('ERR_NETWORK_CHANGED') ||
    msg.includes('NetworkError') ||
    msg.includes('Failed to fetch') ||
    msg.toLowerCase().includes('timeout')
  )
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Ask the server for a job's answer until it has one: { httpStatus, body } (exactly what the generate route would
 * have answered), or null when the server doesn't know the job (switch off, answered on its own request, expired)
 * or the deadline passes. Every second or two at first, then every four seconds; a fresh token each time.
 */
export async function collectImageJob({
  apiBase,
  jobId,
  getToken,
  fetchImpl = fetch,
  wait = sleep,
  now = () => Date.now(),
  deadlineMs = POLL_DEADLINE_MS,
}) {
  const started = now()
  let unknown = 0
  while (now() - started < deadlineMs) {
    await wait(now() - started < 60000 ? 2000 : 4000)
    let res
    try {
      res = await fetchImpl(`${apiBase}/api/image_job/${jobId}`, {
        headers: { Authorization: await getToken() },
      })
    } catch {
      continue // still offline: ask again
    }
    if (res.status === 404) {
      // Twice, in case the first ask beat the server writing the job down.
      unknown += 1
      if (unknown >= 2) return null
      continue
    }
    if (!res.ok) continue
    let data
    try {
      data = await res.json()
    } catch {
      continue
    }
    if (data?.status === 'done') {
      return { httpStatus: data.http_status, body: data.body }
    }
  }
  return null
}

/** A collected answer in the shape of a fetch Response, for code written for the original request. */
export function asResponse({ httpStatus, body }) {
  return {
    ok: httpStatus >= 200 && httpStatus < 300,
    status: httpStatus,
    json: async () => body,
  }
}
